import { useEffect, useMemo, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Menu, X, Mail, Phone, MapPin, Search } from "lucide-react";
import { products, divisionFilters } from "@/products";
import ProductModal from "@/components/ProductModal";
import ProductCard from "@/components/ProductCard";
import ContactForm from "@/components/ContactForm";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/c4204f00-e9bc-4500-a54f-7f8fa7fec95a/images/ade44c5724b432ac75354575a3709644a0f8aaf40a36cee7b1168a53a51f8091.jpeg";
const GALLERY = [
  ["Inside the lab", "Quality begins with curiosity.", "https://static.prod-images.emergentagent.com/jobs/c4204f00-e9bc-4500-a54f-7f8fa7fec95a/images/f9fe8c73cda399bccc0b8436ba7850b61cee14db847c9db61f284ebbbb9d4d22.jpeg"],
  ["Our factory", "Precision at every step.", "https://static.prod-images.emergentagent.com/jobs/c4204f00-e9bc-4500-a54f-7f8fa7fec95a/images/c1e4057aef91a8d0846ed659050112a7d90c69e4733a0e9a2320d21701f37e70.jpeg"],
  ["Our portfolio", "Care, made tangible.", HERO_IMAGE],
];
const navItems = [
  ["home", "Home"], ["story", "Who we are"], ["human", "Human health"], ["animal", "Animal health"],
  ["catalogue", "Catalogue"], ["board", "Board"], ["gallery", "Gallery"], ["vacancy", "Vacancy"], ["contact", "Contact"],
];
const divisions = [
  { number: "01", label: "Human health", title: "Care that moves life forward.", text: "High-quality, affordable branded generics following CGMP and ICH guidelines, made for the specific needs of people across Pakistan.", mark: "H" },
  { number: "02", label: "Livestock & poultry", title: "Healthier animals. Stronger communities.", text: "From livestock to poultry, our veterinary portfolio helps farmers, veterinarians and producers improve wellbeing and productivity.", mark: "A" },
];

function ScrollScene({ children, className = "" }) {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -70]);
  return <motion.div className={className} style={{ y }}>{children}</motion.div>;
}

function Division({ division, jump, index, animal }) {
  return (
    <div className="division-inner">
      <div className="section-index">{index} <span /></div>
      <ScrollScene className="division-copy">
        <p className="eyebrow">{division.number} / {division.label}</p>
        <h2>{division.title}</h2>
        <p>{division.text}</p>
        {animal ? (
          <div className="animal-links">
            <button onClick={() => jump("catalogue")} data-testid="livestock-products-button">
              Livestock <ArrowUpRight size={17} />
            </button>
            <button onClick={() => jump("catalogue")} data-testid="poultry-products-button">
              Poultry <ArrowUpRight size={17} />
            </button>
          </div>
        ) : (
          <button className="text-link" onClick={() => jump("catalogue")} data-testid="human-products-button">
            Explore products <ArrowUpRight size={17} />
          </button>
        )}
      </ScrollScene>
      <div className={`division-mark ${animal ? "green-mark" : "blue-mark"}`}>
        <span>{division.mark}</span>
        <small>{division.label}</small>
      </div>
    </div>
  );
}

function Director({ name, role, mark, text, years, focus }) {
  return (
    <article className="director-card" data-testid={`director-${mark.toLowerCase()}-card`}>
      <div className="director-avatar">{mark}</div>
      <div>
        <p className="eyebrow">{role}</p>
        <h3>{name}</h3>
        <div className="director-tags">
          <span>{years}</span>
          <span>{focus}</span>
        </div>
        <p>{text}</p>
      </div>
      <ArrowUpRight size={18} />
    </article>
  );
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setActive(entry.target.id)),
      { rootMargin: "-35% 0px -55%" },
    );
    navItems.forEach(([id]) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = openProduct ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [openProduct]);

  const jump = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const filtered = useMemo(
    () => products.filter(
      (p) => (filter === "All" || p.division === filter) &&
        `${p.name} ${p.generic}`.toLowerCase().includes(query.toLowerCase()),
    ),
    [query, filter],
  );

  return (
    <main className="site-shell">
      <motion.div className="scroll-progress" style={{ scaleX }} />

      <header className="topbar" data-testid="site-header">
        <button className="brand" onClick={() => jump("home")} data-testid="brand-home-button" aria-label="Go to home">
          <span className="brand-mark" data-testid="brand-logo">N</span>
          <span>Nawan<br /><b>Laboratories</b></span>
        </button>
        <nav className={`desktop-nav ${menuOpen ? "open" : ""}`} data-testid="primary-navigation">
          {navItems.map(([id, label]) => (
            <button key={id} onClick={() => jump(id)} className={active === id ? "active" : ""} data-testid={`nav-${id}-button`}>
              {label}
            </button>
          ))}
          <a href="#contact" onClick={(e) => { e.preventDefault(); jump("contact"); }} className="nav-contact" data-testid="nav-contact-link">
            Get in touch <ArrowUpRight size={15} />
          </a>
        </nav>
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button" aria-label="Toggle menu">
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <section id="home" className="hero section-anchor" data-testid="hero-section">
        <div className="hero-backdrop" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <div className="hero-shade" />
        <ScrollScene className="hero-copy">
          <p className="eyebrow" data-testid="hero-eyebrow">Since 1996 · Pakistan</p>
          <h1 data-testid="hero-heading">Pioneering<br /><em>health</em> in every dimension.</h1>
          <p className="hero-description" data-testid="hero-description">
            Quality pharmaceutical care for people, animals and the future we share.
          </p>
          <button className="circle-cta" onClick={() => jump("story")} data-testid="hero-scroll-button">
            <ArrowDown size={20} />
            <span>Explore our story</span>
          </button>
        </ScrollScene>
        <div className="hero-meta" data-testid="hero-meta">
          <span>01</span><span className="meta-line" /><span>09</span><span>Scroll to explore</span>
        </div>
      </section>

      <section id="story" className="story section-anchor dark-section" data-testid="story-section">
        <div className="section-grid">
          <div className="section-index">02 <span /></div>
          <ScrollScene className="story-copy">
            <p className="eyebrow">Who we are</p>
            <h2>Made with purpose.<br /><span>Built to last.</span></h2>
            <p>Established in 1996, Nawan Pharmaceutical Laboratories is a quality-conscious human and veterinary pharmaceutical manufacturer building a future on growth, innovation and quality products.</p>
            <p>Our modern, high-tech plant produces powders for injection, liquid injections, syrups, suspensions, tablets, capsules and oral liquids for communities across Pakistan and internationally.</p>
            <div className="story-stats">
              <div><strong>1996</strong><small>Established</small></div>
              <div><strong>CGMP</strong><small>Guidelines</small></div>
              <div><strong>02</strong><small>Health worlds</small></div>
            </div>
          </ScrollScene>
          <div className="story-visual" data-testid="story-visual">
            <div className="story-monogram">N<span>LABORATORIES</span></div>
            <div className="orbit orbit-one" />
            <div className="orbit orbit-two" />
          </div>
        </div>
      </section>

      <section id="human" className="division-section section-anchor" data-testid="human-health-section">
        <div className="division-watermark">HUMAN<br />HEALTH</div>
        <Division division={divisions[0]} jump={jump} index="03" />
      </section>

      <section id="animal" className="division-section animal-section section-anchor" data-testid="animal-health-section">
        <div className="division-watermark">ANIMAL<br />HEALTH</div>
        <Division division={divisions[1]} jump={jump} index="04" animal />
      </section>

      <section id="catalogue" className="catalogue-section section-anchor" data-testid="catalogue-section">
        <div className="catalogue-intro">
          <div className="section-index">05 <span /></div>
          <div>
            <p className="eyebrow">The Nawan catalogue</p>
            <h2>Find the right<br /><span>care by name.</span></h2>
            <p>Browse a growing portfolio across human health, livestock and poultry. Search by product name or active ingredient — tap any card to view literature and download a PDF pack insert.</p>
          </div>
        </div>
        <div className="catalogue-controls">
          <label className="catalogue-search">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search products or ingredients"
              data-testid="catalogue-search-input"
            />
          </label>
          <div className="filter-tabs" data-testid="catalogue-filter-tabs">
            {divisionFilters.map((item) => (
              <button
                key={item}
                className={filter === item ? "selected" : ""}
                onClick={() => setFilter(item)}
                data-testid={`catalogue-filter-${item.toLowerCase().replaceAll(" ", "-")}`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <p className="catalogue-count" data-testid="catalogue-result-count">
          Showing {filtered.length} of {products.length} products
        </p>
        <div className="catalogue-grid">
          {filtered.map((product, index) => (
            <ProductCard product={product} index={index} key={product.name} onOpen={setOpenProduct} />
          ))}
        </div>
        {!filtered.length && (
          <div className="empty-results" data-testid="catalogue-empty-state">
            No products match that search. Try a brand name or active ingredient.
          </div>
        )}
      </section>

      <section id="board" className="board-section section-anchor dark-section" data-testid="board-section">
        <div className="board-intro">
          <div className="section-index">06 <span /></div>
          <div>
            <p className="eyebrow">Board of directors</p>
            <h2>Experience that<br /><span>keeps us moving.</span></h2>
          </div>
        </div>
        <div className="director-grid">
          <Director
            name="Mr. Naseer Awan"
            role="Founder & Chairman"
            mark="NA"
            years="30+ years"
            focus="Strategy · Manufacturing"
            text="Scholastic and practical knowledge of Pakistan's health sector made him an authority in pharmaceuticals. He laid the foundation of Nawan in 1996 with a clear vision, commitment and hard work."
          />
          <Director
            name="Mr. Ehsan Awan"
            role="Director"
            mark="EA"
            years="15+ years"
            focus="Growth · International markets"
            text="Ehsan Awan brings deep experience in entrepreneurship and corporate vision. He has helped bring innovative ventures to Nawan and open the company to international markets."
          />
        </div>
      </section>

      <section id="gallery" className="gallery-section section-anchor" data-testid="gallery-section">
        <div className="gallery-heading">
          <div className="section-index">07 <span /></div>
          <div>
            <p className="eyebrow">Inside Nawan</p>
            <h2>People, place,<br /><span>precision.</span></h2>
          </div>
        </div>
        <div className="gallery-grid">
          {GALLERY.map(([title, caption, image], index) => (
            <button
              className={`gallery-tile gallery-tile-${index + 1}`}
              key={title}
              onClick={() => setLightbox([title, caption, image])}
              data-testid={`gallery-tile-${index + 1}`}
            >
              <img src={image} alt={title} />
              <span>
                <small>{title}</small>
                {caption}
                <ArrowUpRight size={17} />
              </span>
            </button>
          ))}
        </div>
      </section>

      <section id="vacancy" className="vacancy-section section-anchor" data-testid="vacancy-section">
        <div className="vacancy-panel">
          <div className="vacancy-number">08</div>
          <div>
            <p className="eyebrow">Careers at Nawan</p>
            <h2>We're<br /><em>hiring.</em></h2>
            <p>If you want to be part of the Nawan team, we are waiting for your resume. Share your CV with our recruitment team and tell us the role you're most excited about.</p>
            <div className="vacancy-links">
              <a href="mailto:hr@nawanlab.com" data-testid="vacancy-hr-link">hr@nawanlab.com <ArrowUpRight size={16} /></a>
              <a href="mailto:recruitment@nawanlab.com" data-testid="vacancy-recruitment-link">recruitment@nawanlab.com <ArrowUpRight size={16} /></a>
            </div>
          </div>
          <div className="vacancy-stamp">JOIN<br /><span>US</span></div>
        </div>
      </section>

      <section id="contact" className="contact-section section-anchor dark-section" data-testid="contact-section">
        <div className="contact-grid">
          <div className="contact-copy">
            <div className="section-index">09 <span /></div>
            <p className="eyebrow">Get in touch</p>
            <h2>Let's start a<br /><span>conversation.</span></h2>
            <p>Send us a short message and the right team will get back to you within one business day.</p>
            <div className="contact-meta">
              <div>
                <span><MapPin size={14} /> Head office</span>
                <p>136–138, Sector 15, Korangi Industrial Area,<br />Karachi, Pakistan.</p>
              </div>
              <div>
                <span><Phone size={14} /> Phone</span>
                <p>+92 (21) 35065835<br />+92 (21) 35059000</p>
              </div>
              <div>
                <span><Mail size={14} /> Email</span>
                <p>info@nawanlab.com</p>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="footer" data-testid="footer">
        <div className="footer-top">
          <span className="footer-mark" data-testid="footer-logo">N</span>
          <p>Advancing health.<br /><em>Together.</em></p>
          <a href="mailto:info@nawanlab.com" className="footer-arrow" data-testid="footer-email-link">
            <Mail size={20} />
          </a>
        </div>
        <div className="footer-bottom">
          <span>© 2021 Nawan Laboratories (Pvt.) Limited</span>
          <span>Quality · Collaboration · Community · Innovation</span>
        </div>
      </footer>

      <ProductModal product={openProduct} onClose={() => setOpenProduct(null)} />

      {lightbox && (
        <div className="lightbox" role="dialog" aria-modal="true" data-testid="gallery-lightbox">
          <button className="lightbox-close" onClick={() => setLightbox(null)} data-testid="gallery-lightbox-close">
            <X />
          </button>
          <img src={lightbox[2]} alt={lightbox[0]} />
          <div>
            <p className="eyebrow">{lightbox[0]}</p>
            <h3>{lightbox[1]}</h3>
          </div>
        </div>
      )}
    </main>
  );
}
